import { useMemo } from 'react';

import { CurrencyFormatter } from '../../../../formatters/CurrencyFormatter';
import { usePromotionsV2Slice } from '../../../../store/slices/promotionsV2/selectors';
import type { Trophy } from '../../../../store/slices/promotionsV2/types';
import { isPromoActiveNow } from '../../../../store/slices/promotionsV2/utils';
import { getRoundLimitMessage } from '../utils';

/* 
    Promo Eligbility :
      1. round limit should not exceeds
      2. bet amount should be greater than or equal to minimum bet of a promo
*/
export const usePromoTrophyConnected = (): Trophy => {
  const gameData = usePromotionsV2Slice.getPromosGameData();
  const language = usePromotionsV2Slice.getPromosUserData()?.lang as string;
  const promotions = usePromotionsV2Slice.getOptedPromotions();
  const activePromotions = promotions.filter((promo) => isPromoActiveNow(promo));

  const points = usePromotionsV2Slice.getPoints();
  const trophy = useMemo(() => {
    const totalBet = gameData?.totalBet;
    const currency = gameData?.currency as string;
    const isBetOpenStatus = gameData?.isBetOpenStatus;

    if (isBetOpenStatus || !totalBet || !activePromotions.length) return { enable: false };

    const isRoundLimitExceeded = points.length > 0 ? points.every((p) => p.isRoundLimitExceeded) : false;
    if (isRoundLimitExceeded) {
      const earlyExpiredPromo = activePromotions.reduce((earliest, promo) => {
        if (promo.activeDuration.toEpoch < earliest.activeDuration.toEpoch) {
          return promo;
        }
        return earliest;
      });
      return {
        enable: true,
        isActive: false,
        toolTipContent: getRoundLimitMessage(earlyExpiredPromo.roundsType),
      };
    }

    const minBets =
      points.length > 0
        ? points.filter((point) => !point.isRoundLimitExceeded).map((point) => point.minimumBetLimit)
        : activePromotions.map((promo) => promo.prizeDetails.minimumBetLimit);
    const lowestMinBet = minBets.reduce((acc, bet) => (bet < acc ? bet : acc));
    if (totalBet < lowestMinBet) {
      const formattedMinBetInAll = CurrencyFormatter.format(lowestMinBet, language, {
        currency,
      });
      return {
        enable: true,
        isActive: false,
        toolTipContent: `PROMO_BET_BELOWMIN`,
        minBet: formattedMinBetInAll,
      };
    }

    return {
      enable: true,
      isActive: true,
    };
  }, [gameData, points, language, promotions.length]);

  return trophy;
};
